import React from 'react';
import { toBracketGames, winningPathLength } from './BracketTree';
import RoundHeaderBand from './RoundHeaderBand';

/**
 * Cuadro a dos lados (estilo NCAA): la final en el centro y las dos mitades
 * abriéndose hacia los lados. La mitad derecha se pinta en modo `mirror`.
 */
export default function TwoSidedBracketTree(props) {
  const {
    GameComponent,
    game,
    gameDimensions,
    svgPadding = 14,
    roundSeparatorWidth = 40,
    lineInfo = { yOffset: -8, separation: 8, homeVisitorSpread: 14 },
    LineComponent,
    className,
    svgProps,
    headerColumns = null,
    bandHeight = 0,
    thirdPlaceGame = null,
    ...rest
  } = props;

  const { width: gameW, height: gameH } = gameDimensions;
  const homeChild = game?.sides?.home?.seed?.sourceGame;
  const visitorChild = game?.sides?.visitor?.seed?.sourceGame;

  const leftRounds = winningPathLength(homeChild);
  const rightRounds = winningPathLength(visitorChild);
  const halfRounds = Math.max(leftRounds, rightRounds, 1);

  const innerHeight = gameH * Math.pow(2, halfRounds - 1) + svgPadding * 2;
  const totalCols = leftRounds + 1 + rightRounds;
  const svgWidth = totalCols * gameW + (totalCols - 1) * roundSeparatorWidth + svgPadding * 2;
  const step = gameW + roundSeparatorWidth;

  const centerY = bandHeight + innerHeight / 2 - gameH / 2;
  const leftRootX = svgPadding + (leftRounds - 1) * step;
  const finalX = svgPadding + leftRounds * step;
  const rightRootX = svgPadding + (leftRounds + 1) * step;

  const leftGames = toBracketGames({
    GameComponent,
    game: homeChild,
    x: leftRootX,
    y: centerY,
    gameDimensions,
    roundSeparatorWidth,
    round: leftRounds,
    lineInfo,
    homeOnTop: true,
    LineComponent,
    mirror: false,
    ...rest,
  });

  const rightGames = toBracketGames({
    GameComponent,
    game: visitorChild,
    x: rightRootX,
    y: centerY,
    gameDimensions,
    roundSeparatorWidth,
    round: rightRounds,
    lineInfo,
    homeOnTop: true,
    LineComponent,
    mirror: true,
    ...rest,
  });

  const Connector = LineComponent || 'path';
  const cY = centerY + gameH / 2;
  const leftConnD = `M${leftRootX + gameW} ${cY} H${finalX}`;
  const rightConnD = `M${finalX + gameW} ${cY} H${rightRootX}`;

  // 3º/4º puesto: debajo de la final (mantener en sync con bracketRounds.js).
  let svgHeight = innerHeight + bandHeight;
  let thirdPlaceNode = null;
  if (thirdPlaceGame) {
    const tpY = centerY + gameH + Math.round(gameH * 0.5);
    svgHeight = Math.max(svgHeight, tpY + gameH + svgPadding);
    thirdPlaceNode = (
      <g key="third-place">
        <GameComponent {...rest} {...gameDimensions} game={thirdPlaceGame} x={finalX} y={tpY} homeOnTop />
      </g>
    );
  }

  const raw = svgProps || {};
  const { style: incomingStyle, width: _ignoreW, height: _ignoreH, ...restSvgAttrs } = raw;
  const mergedStyle =
    incomingStyle && typeof incomingStyle === 'object' && !Array.isArray(incomingStyle)
      ? { ...incomingStyle }
      : {};
  delete mergedStyle.width;
  delete mergedStyle.height;

  const showBand = bandHeight > 0 && headerColumns;

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      {...restSvgAttrs}
      width={svgWidth}
      height={svgHeight}
      style={mergedStyle}
      className={className}
    >
      {showBand ? <RoundHeaderBand columns={headerColumns} bandHeight={bandHeight} width={svgWidth} /> : null}
      <g>{leftGames}</g>
      <g>{rightGames}</g>
      {Connector != null ? <Connector d={leftConnD} /> : null}
      {Connector != null ? <Connector d={rightConnD} /> : null}
      <g>
        <GameComponent {...rest} {...gameDimensions} game={game} x={finalX} y={centerY} homeOnTop />
      </g>
      {thirdPlaceNode}
    </svg>
  );
}
